// Grafo interativo da Dobra de Nicho (@xyflow/react).
// Centro = canal; em volta = oportunidades tipadas. Clicar numa Dobra adiciona
// +4 nos de nicho-alvo no proprio canvas (fan out). Tudo arrastavel, com pan/zoom.
//
// Regras de rotulo:
//  - DENTRO da bola: so o NOME DA CLASSE (CANAL / NOVA VISAO / SUBNICHO / DOBRA /
//    ADAPTACAO) — texto estavel, nao gerado por IA.
//  - FORA (embaixo): o nome especifico gerado pela IA (label da oportunidade,
//    nome do nicho). Assim a bola fica limpa e o texto de IA nao polui.
//
// Geometria das linhas: as arestas conectam SEMPRE centro-de-bola -> centro-de-bola
// (handles ancorados no centro do no). Como as bolas tem fundo OPACO, a parte da
// linha sob a bola fica ESCONDIDA — cada linha aparece saindo da BORDA da bola
// central rumo a cada oportunidade (raios harmonicos). Nichos saem da Dobra.
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ReactFlow, Background, Controls, Handle, Position,
  useNodesState, useEdgesState, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './nichoFoldGraph.css';   // override dark theme dos Controls (+/- visíveis)
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

const TYPE_COLOR = {
  mar_de_ideias: '#06B6D4',       // MAR DE IDEIAS (mesmo território) — cyan
  nova_visao: '#06B6D4',          // legado (vira mar_de_ideias)
  subnicho: '#06B6D4',            // legado
  mar_oportunidades: '#0DAEFF',   // legado (lançador antigo)
  dobra: '#F59E0B',               // CROSS-NICHE (lançador) — âmbar
  adaptacao_mercado: '#34D399',   // verde do DS (--dp-green-500)
};
// Lançadores: bolas que, ao expandir, abrem 4 filhos no canvas.
const LAUNCHERS = { dobra: '#F59E0B', mar_oportunidades: '#0DAEFF' };

// Diametros das bolas. So o nome da classe vai dentro -> bolas compactas.
const CENTER_D = 116;
const OPP_D = 86;
const NICHE_D = 60;
const NICHE_SPREAD = 120;        // leque dos nichos

// Handle ancorado no CENTRO do no -> arestas (retas) ligam centro a centro,
// produzindo raios que apontam pra direcao de cada bola. Invisivel e sem clique.
const centerHandle = {
  width: 1, height: 1, minWidth: 1, minHeight: 1,
  left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
  background: 'transparent', border: 'none', opacity: 0, pointerEvents: 'none',
};

// Rotulo FORA da bola (embaixo). Absolute -> nao entra no bounding box do no,
// entao as arestas continuam conectando no centro da bola.
const labelBelow = {
  position: 'absolute', top: 'calc(100% + 7px)', left: '50%',
  transform: 'translateX(-50%)', textAlign: 'center', pointerEvents: 'none',
};

const clampText = (lines) => ({
  display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden',
});

// Fundo OPACO (esconde a linha que passa por baixo) + leve brilho no topo.
// Duas camadas: base SOLIDA var(--dp-bg-card) (esconde a aresta 100%) + brilho
// colorido por cima que some em transparent (sem deixar a linha vazar no centro).
const opaqueBall = (color, glow) => ({
  background: `radial-gradient(circle at 50% 30%, ${color}${glow}, transparent 68%), var(--dp-bg-card)`,
  border: `2px solid ${color}`,
});

// Hover/foco premium: leve lift + brilho mais forte, sem re-render (muta o DOM).
const hoverOn = (color) => (e) => {
  e.currentTarget.style.transform = 'scale(1.07)';
  e.currentTarget.style.boxShadow = `0 0 0 4px ${color}26, 0 6px 22px rgba(0,0,0,0.5)`;
};
const hoverOff = (e, shadow) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.boxShadow = shadow;
};

function CenterNode({ data }) {
  const baseShadow = '0 2px 16px rgba(0,0,0,0.5)';
  const activate = () => data.handlersRef?.current?.onCenterClick?.();
  return (
    <div style={{ width: CENTER_D, height: CENTER_D, position: 'relative', cursor: 'pointer', fontFamily: 'var(--dp-font)' }}>
      <Handle type="source" position={Position.Right} style={centerHandle} />
      <div
        role="button"
        tabIndex={0}
        aria-label={`Canal: ${data.title || ''} — ver análise profunda`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
        onMouseEnter={hoverOn('#0DAEFF')}
        onMouseLeave={(e) => hoverOff(e, baseShadow)}
        onFocus={hoverOn('#0DAEFF')}
        onBlur={(e) => hoverOff(e, baseShadow)}
        style={{
          width: CENTER_D, height: CENTER_D, borderRadius: '50%', ...opaqueBall('#0DAEFF', '33'),
          boxShadow: baseShadow, transition: 'transform .16s ease, box-shadow .16s ease', outline: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: 14,
        }}
      >
        {data.thumb && (
          <img src={data.thumb} alt="" referrerPolicy="no-referrer" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', marginBottom: 5, border: '1px solid rgba(13,174,255,0.5)' }} />
        )}
        <div style={{ fontSize: 8.5, fontWeight: 700, color: '#0DAEFF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Canal</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1.15, ...clampText(2) }}>
          {data.title || '—'}
        </div>
      </div>
    </div>
  );
}

function OpportunityNode({ data }) {
  const color = data.color || '#0DAEFF';
  const isVirada = !data.loading && (data.etiquetas || []).includes('virada');
  const baseShadow = '0 2px 13px rgba(0,0,0,0.45)';
  const activate = () => data.handlersRef?.current?.onOpportunityClick?.(data.index);
  return (
    <div style={{ width: OPP_D, height: OPP_D, position: 'relative', cursor: 'pointer', fontFamily: 'var(--dp-font)' }}>
      <Handle type="target" position={Position.Left} style={centerHandle} />
      <Handle type="source" position={Position.Right} style={centerHandle} />
      {isVirada && (
        <span style={{
          position: 'absolute', top: -3, left: '50%', transform: 'translateX(-50%)', zIndex: 3,
          fontSize: 8, fontWeight: 700, color: '#fff', background: '#F87171',
          padding: '1px 7px', borderRadius: 999, textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>Virada</span>
      )}
      <div
        role="button"
        tabIndex={0}
        aria-label={`${data.typeLabel}: ${data.label}`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
        onMouseEnter={hoverOn(color)}
        onMouseLeave={(e) => hoverOff(e, baseShadow)}
        onFocus={hoverOn(color)}
        onBlur={(e) => hoverOff(e, baseShadow)}
        style={{
          width: OPP_D, height: OPP_D, borderRadius: '50%', ...opaqueBall(color, '30'),
          boxShadow: baseShadow, transition: 'transform .16s ease, box-shadow .16s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none',
          textAlign: 'center', padding: 8,
        }}
      >
        {data.loading
          ? <Loader2 size={18} className="animate-spin" style={{ color }} />
          : <span style={{ fontSize: 9.5, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.15, ...clampText(3) }}>{data.typeLabel}</span>}
      </div>
      {/* nome especifico (gerado por IA) FORA da bola */}
      <div style={{ ...labelBelow, width: data.labelW || 168 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--dp-text-primary)', lineHeight: 1.25, ...clampText(2) }}>{data.label}</div>
      </div>
    </div>
  );
}

function NicheNode({ data }) {
  const amber = data.color || '#F59E0B';   // âmbar (filho de dobra) ou azul (filho de mar)
  const baseShadow = '0 2px 11px rgba(0,0,0,0.42)';
  const activate = () => data.handlersRef?.current?.onNicheClick?.(data.oppIndex, data.nicheIndex);
  return (
    <div style={{ width: NICHE_D, height: NICHE_D, position: 'relative', cursor: 'pointer', fontFamily: 'var(--dp-font)' }}>
      <Handle type="target" position={Position.Left} style={centerHandle} />
      <div
        role="button"
        tabIndex={0}
        aria-label={data.nome}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
        onMouseEnter={hoverOn(amber)}
        onMouseLeave={(e) => hoverOff(e, baseShadow)}
        onFocus={hoverOn(amber)}
        onBlur={(e) => hoverOff(e, baseShadow)}
        style={{
          width: NICHE_D, height: NICHE_D, borderRadius: '50%', ...opaqueBall(amber, '2b'),
          boxShadow: baseShadow, transition: 'transform .16s ease, box-shadow .16s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', outline: 'none',
        }}
      >
        {data.loading
          ? <Loader2 size={15} className="animate-spin" style={{ color: amber }} />
          : <span style={{ fontSize: 16, fontWeight: 800, color: amber }}>{(data.nicheIndex ?? 0) + 1}</span>}
      </div>
      {/* nome do nicho (gerado por IA) FORA da bola */}
      <div style={{ ...labelBelow, width: data.labelW || 144 }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--dp-text-primary)', lineHeight: 1.2, ...clampText(3) }}>{data.nome}</div>
      </div>
    </div>
  );
}

const nodeTypes = { center: CenterNode, opportunity: OpportunityNode, niche: NicheNode };

// Reenquadra o canvas quando novos nos de nicho aparecem (efeito de leque
// abrindo). Vive DENTRO do <ReactFlow> pra ter acesso ao useReactFlow.
function FitOnNiches({ count }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (count <= 0) return undefined;
    const id = requestAnimationFrame(() => fitView({ padding: 0.24, duration: 420 }));
    return () => cancelAnimationFrame(id);
  }, [count, fitView]);
  return null;
}

// Reenquadra as bolas quando o drawer abre/troca/fecha (signal muda). O <main>
// anima o paddingRight (~0.3s) ao abrir o painel, encolhendo o canvas; esperamos
// a transicao terminar ANTES do fitView, senao reenquadra no tamanho antigo.
// Pula a 1a execucao (montagem) — o fitView inicial ja vem do <ReactFlow fitView>.
function FitOnSignal({ signal, padding }) {
  const { fitView } = useReactFlow();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const id = setTimeout(() => fitView({ padding, duration: 400 }), 340);
    return () => clearTimeout(id);
  }, [signal, padding, fitView]);
  return null;
}

export default function NichoFoldGraph({
  channelTitle, channelThumb, oportunidades, reports, nicheLoading, onOpportunityClick, onNicheClick, onCenterClick, loadingIndex, isMobile, fitSignal,
}) {
  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const addedLaunchers = useRef(new Set());   // lançadores já expandidos (dobra/mar)
  const oppCenterRef = useRef({});

  // Handlers num ref estavel -> os nos leem sempre a versao mais recente (sem
  // congelar closure stale ao serem criados no efeito base).
  const handlersRef = useRef({ onOpportunityClick, onNicheClick, onCenterClick });
  handlersRef.current = { onOpportunityClick, onNicheClick, onCenterClick };

  const R = isMobile ? 158 : 215;          // raio do anel (bolas mais perto do centro = linhas curtas)
  const NICHE_DIST = isMobile ? 165 : 205;
  const labelW = isMobile ? 120 : 168;

  // Grafo base: centro + oportunidades em raios harmonicos. A DOBRA fica SEMPRE
  // a direita; as demais no arco esquerdo. position = canto sup. esq.; -D/2 faz
  // o CENTRO da bola cair no ponto radial (linhas perfeitamente radiais).
  // IMPORTANTE: deps SO [oportunidades] — nao depende de `t`/channelTitle, senao
  // trocar idioma re-executava a base e apagava os nichos ja adicionados. Os
  // textos reativos (titulo do canal, rotulo do tipo) entram no displayNodes.
  useEffect(() => {
    addedLaunchers.current = new Set();
    oppCenterRef.current = {};
    const baseNodes = [{
      id: 'center', type: 'center', focusable: false,
      position: { x: -CENTER_D / 2, y: -CENTER_D / 2 },
      data: { title: channelTitle, handlersRef },
    }];
    const baseEdges = [];

    const place = (i, op, cx, cy) => {
      oppCenterRef.current[i] = { x: cx, y: cy };
      baseNodes.push({
        id: `op-${i}`, type: 'opportunity', focusable: false,
        position: { x: cx - OPP_D / 2, y: cy - OPP_D / 2 },
        data: {
          index: i, tipo: op.tipo,
          color: TYPE_COLOR[op.tipo] || '#0DAEFF',
          label: op.label, etiquetas: op.etiquetas || [],
          confianca: op.confianca, handlersRef, labelW,
        },
      });
      baseEdges.push({ id: `e-c-${i}`, source: 'center', target: `op-${i}` });
    };

    // Dois lançadores nos polos: Dobra à DIREITA, Mar de Oportunidades à
    // ESQUERDA (cada um abre os 4 filhos pro seu lado). Os demais (nova_visao,
    // adaptacao) ficam nos arcos de CIMA e BAIXO, sem colidir com os lançadores.
    const dobraI = oportunidades.findIndex((o) => o.tipo === 'dobra');
    const marI = oportunidades.findIndex((o) => o.tipo === 'mar_oportunidades');
    if (dobraI >= 0) place(dobraI, oportunidades[dobraI], R, 0);    // direita
    if (marI >= 0) place(marI, oportunidades[marI], -R, 0);         // esquerda

    const others = oportunidades
      .map((op, i) => ({ op, i }))
      .filter(({ i }) => i !== dobraI && i !== marI);
    const m = others.length;
    const half = Math.ceil(m / 2);
    others.forEach(({ op, i }, k) => {
      let deg;
      if (k < half) {                       // arco de BAIXO (40°..140°)
        deg = half <= 1 ? 90 : 40 + (k / (half - 1)) * 100;
      } else {                              // arco de CIMA (220°..320°)
        const kk = k - half, topN = m - half;
        deg = topN <= 1 ? 270 : 220 + (kk / (topN - 1)) * 100;
      }
      const ang = (deg * Math.PI) / 180;
      place(i, op, Math.round(R * Math.cos(ang)), Math.round(R * Math.sin(ang)));
    });

    setNodes(baseNodes);
    setEdges(baseEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oportunidades, R, NICHE_DIST, labelW, setNodes, setEdges]);

  // Quando uma Dobra expande (report chega), adiciona os 4 nos de nicho em leque
  // pro lado de fora (linhas saem do centro da Dobra).
  useEffect(() => {
    Object.entries(reports || {}).forEach(([idxStr, report]) => {
      const idx = Number(idxStr);
      // Ambos os lançadores (dobra E mar_oportunidades) abrem 4 filhos.
      if (!report || !LAUNCHERS[report.tipo] || addedLaunchers.current.has(idx)) return;
      const niches = report.nichos_sugeridos || [];
      if (!niches.length) return;
      addedLaunchers.current.add(idx);
      const childColor = LAUNCHERS[report.tipo];   // âmbar (dobra) ou azul (mar)
      const P = oppCenterRef.current[idx] || { x: 0, y: 0 };
      const len = Math.hypot(P.x, P.y) || 1;
      const ox = P.x / len, oy = P.y / len;     // direcao radial (pra fora)
      const ux = -oy, uy = ox;                  // perpendicular (leque)
      const newNodes = [];
      const newEdges = [];
      niches.forEach((nch, j) => {
        const off = (j - (niches.length - 1) / 2) * NICHE_SPREAD;
        const cx = P.x + ox * NICHE_DIST + ux * off;
        const cy = P.y + oy * NICHE_DIST + uy * off;
        const id = `niche-${idx}-${j}`;
        newNodes.push({
          id, type: 'niche', focusable: false,
          position: { x: Math.round(cx - NICHE_D / 2), y: Math.round(cy - NICHE_D / 2) },
          data: { oppIndex: idx, nicheIndex: j, nome: nch.nome, count: (nch.titulos || []).length, color: childColor, handlersRef, labelW: isMobile ? 110 : 144 },
        });
        newEdges.push({ id: `e-${idx}-${j}`, source: `op-${idx}`, target: id, style: { stroke: `${childColor}73`, strokeWidth: 1.5 } });
      });
      setNodes((nds) => [...nds, ...newNodes]);
      setEdges((eds) => [...eds, ...newEdges]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, NICHE_DIST, isMobile, setNodes, setEdges]);

  const onNodeClick = useCallback((_e, node) => {
    if (node.type === 'opportunity') onOpportunityClick(node.data.index);
    else if (node.type === 'niche') onNicheClick(node.data.oppIndex, node.data.nicheIndex);
    else if (node.type === 'center') onCenterClick?.();
  }, [onOpportunityClick, onNicheClick, onCenterClick]);

  // Reinjeta estado DINAMICO e textos REATIVOS (titulo do canal, rotulo
  // traduzido, loading, contagem) a cada render — sem destruir o grafo.
  const displayNodes = useMemo(
    () => nodes.map((nd) => {
      if (nd.type === 'center') {
        return { ...nd, data: { ...nd.data, title: channelTitle, thumb: channelThumb } };
      }
      if (nd.type === 'opportunity') {
        return { ...nd, data: { ...nd.data, loading: loadingIndex === nd.data.index, typeLabel: t(`dobraNicho.type_${nd.data.tipo}`, nd.data.tipo) } };
      }
      if (nd.type === 'niche') {
        const niche = reports[nd.data.oppIndex]?.nichos_sugeridos?.[nd.data.nicheIndex];
        const count = (niche?.titulos || []).length;
        const loading = !!(nicheLoading || {})[`${nd.data.oppIndex}.${nd.data.nicheIndex}`];
        return { ...nd, data: { ...nd.data, count, loading } };
      }
      return nd;
    }),
    [nodes, loadingIndex, reports, nicheLoading, channelTitle, channelThumb, t],
  );

  const nicheCount = useMemo(() => displayNodes.filter((n) => n.type === 'niche').length, [displayNodes]);

  return (
    <div style={{
      // Reserva ~300px pro que fica acima (header + DNA + "como aplicar" recolhido)
      // pra o grafo caber na viewport SEM rolagem — o dono pediu ele mais visível.
      width: '100%', height: 'min(74vh, calc(100vh - 300px))', minHeight: 440,
      borderRadius: 14, border: '1px solid var(--dp-border-subtle)',
      background: 'var(--dp-bg-primary)', overflow: 'hidden',
    }}>
      <ReactFlow
        nodes={displayNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable
        nodesConnectable={false}
        fitView
        fitViewOptions={{ padding: isMobile ? 0.32 : 0.24 }}
        minZoom={0.3}
        maxZoom={1.6}
        defaultEdgeOptions={{ type: 'straight', style: { stroke: 'rgba(255,255,255,0.12)', strokeWidth: 1.5 } }}
        proOptions={{ hideAttribution: true }}
      >
        <FitOnNiches count={nicheCount} />
        <FitOnSignal signal={fitSignal} padding={isMobile ? 0.32 : 0.24} />
        <Background color="rgba(255,255,255,0.04)" gap={28} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
